import { useState } from 'react'
import { Link } from 'react-router-dom'
import { ToolCard } from '../components/tools/ToolCard'
import { Seo } from '../components/seo/Seo'
import { categories } from '../data/categories'
import { NO_ACCOUNT_PROMISE } from '../data/siteCopy'
import { searchAvailableTools, tools } from '../data/tools'

export function HomePage() {
  const [query, setQuery] = useState('')
  const featuredTools = tools.filter((tool) => tool.available && tool.featured)
  const toolsToTry = ['passport-photo-maker', 'image-metadata-viewer', 'color-palette-generator', 'remove-image-metadata', 'grade-calculator', 'qr-code-scanner'].map((id) => tools.find((tool) => tool.id === id)).filter((tool): tool is NonNullable<typeof tool> => Boolean(tool?.available))
  const financeTools = tools.filter((tool) => tool.category === 'finance' && tool.available)
  const availableCategories = categories.filter((category) => category.available)
  const availableToolCount = tools.filter((tool) => tool.available).length
  const matches = searchAvailableTools(query)

  return (
    <>
      <Seo route="/" />

      <section className="home-hero">
        <div className="shell home-hero__grid">
          <div className="home-hero__copy">
            <p className="eyebrow">Private by design</p>
            <h1>Essential tools.<br /><em>Your data stays on your device.</em></h1>
            <p className="home-hero__lede">Process files and sensitive inputs locally. No upload to an application server, and no account or sign-up required.</p>
            <div className="home-hero__actions">
              <a className="button button--primary button--large" href="#available-tools">Explore all tools <span aria-hidden="true">→</span></a>
              <Link className="text-link" to="/privacy/">How privacy works <span aria-hidden="true">↗</span></Link>
            </div>
            <ul className="home-hero__trust" aria-label="StayInBrowser benefits">
              <li><span aria-hidden="true">✓</span><strong>Not uploaded to any server</strong></li>
              <li><span aria-hidden="true">✓</span><strong>Processed locally</strong></li>
              <li><span aria-hidden="true">✓</span><strong>{NO_ACCOUNT_PROMISE}</strong></li>
            </ul>
          </div>

          <div className="hero-demo" aria-label="Illustration of data processing locally on your device">
            <div className="hero-demo__window">
              <div className="hero-demo__bar"><span /><span /><span /><small>stay in browser</small></div>
              <div className="hero-demo__content">
                <div className="hero-demo__heading">
                  <div><p className="hero-demo__label">Essential tool collection</p><strong>{availableToolCount} tools, no account required</strong></div>
                  <span>Local</span>
                </div>
                <div className="hero-demo__category-grid" aria-label={`${availableCategories.length} available tool categories`}>
                  {availableCategories.map((category) => (
                    <div className={`hero-demo__category hero-demo__category--${category.accent}`} key={category.id}>
                      <b aria-hidden="true">{category.shortLabel}</b>
                      <small>{category.name}</small>
                    </div>
                  ))}
                </div>
                <div className="hero-demo__flow" aria-label="Inputs are processed in the browser and returned as results">
                  <span>Your input</span><b aria-hidden="true">→</b><span>Browser processing</span><b aria-hidden="true">→</b><span>Your result</span>
                </div>
                <div className="hero-demo__summary"><span aria-hidden="true">✓</span> Files stay on your device · No account</div>
              </div>
            </div>
            <div className="hero-demo__privacy"><span aria-hidden="true">●</span> Your data stays on your device</div>
          </div>
        </div>
      </section>

      <section id="available-tools" className="section tool-search-section" aria-labelledby="tool-search-title">
        <div className="shell">
          <div className="section-heading">
            <div><p className="eyebrow">Find a tool</p><h2 id="tool-search-title">Search the local tool collection</h2></div>
            <p>Search happens instantly on this page. The query is not uploaded to any server.</p>
          </div>
          <label className="tool-search">
            <span className="visually-hidden">Search tools</span>
            <input type="search" value={query} onChange={(event) => setQuery(event.target.value)} placeholder="Search tools…" />
          </label>
          {query && <div className="image-tools-grid" aria-live="polite">{matches.length ? matches.map((tool) => <ToolCard key={tool.id} tool={tool} />) : <p>No tools match “{query}”.</p>}</div>}
        </div>
      </section>

      <section className="section section--categories" aria-labelledby="available-tools-title">
        <div className="shell">
          <div className="section-heading">
            <div>
              <p className="eyebrow">Available now</p>
              <h2 id="available-tools-title">Choose the tools you need</h2>
            </div>
            <p>Open any available tool without signing in. Image, PDF, data, developer, calculator, media, business, QR, device, text, and spending workflows run locally.</p>
          </div>
          <div className="category-grid category-grid--available">
            {availableCategories.map((category) => (
              <article className={`category-card category-card--${category.accent} category-card--available`} key={category.id}>
                <div className="category-card__topline">
                  <span className="category-card__symbol" aria-hidden="true">{category.shortLabel}</span>
                  <span className="status-chip status-chip--available">Available</span>
                </div>
                <h3>{category.name}</h3>
                <p>{category.description}</p>
                {category.id === 'images' && <Link className="text-link" to="/images/">View Image tools →</Link>}
                {category.id === 'pdf' && <Link className="text-link" to="/pdf/">View PDF tools →</Link>}
                {category.id === 'developer' && <Link className="text-link" to="/developer/">View Developer tools →</Link>}
                {category.id === 'data' && <Link className="text-link" to="/data/">View Data tools →</Link>}
                {category.id === 'finance' && <Link className="text-link" to="/spending-tracker/">Open Spending Tracker →</Link>}
                {category.id === 'qr' && <Link className="text-link" to="/qr/">View QR tools →</Link>}
                {category.id === 'text' && <Link className="text-link" to="/text/">View Text tools →</Link>}
                {category.id === 'calculators' && <Link className="text-link" to="/calculators/">View calculators →</Link>}
                {category.id === 'device' && <Link className="text-link" to="/device/">View Device tools →</Link>}
                {category.id === 'business' && <Link className="text-link" to="/business/">View Business tools →</Link>}
                {category.id === 'media' && <Link className="text-link" to="/media/">View Media tools →</Link>}
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="section section--featured" aria-labelledby="tools-to-try-title">
        <div className="shell">
          <div className="section-heading">
            <div><p className="eyebrow">Tools to try</p><h2 id="tools-to-try-title">Six practical local workflows</h2></div>
            <p>Prepare document photos, inspect or remove metadata, extract colors, calculate grades, and review QR content without uploading the working input.</p>
          </div>
          <div className="image-tools-grid">{toolsToTry.map((tool) => <ToolCard key={tool.id} tool={tool} />)}</div>
        </div>
      </section>

      <section className="section section--featured">
        <div className="shell">
          <div className="section-heading">
            <div>
              <p className="eyebrow">Featured tools</p>
              <h2>A useful place to start</h2>
            </div>
            <p>Editorially featured tools—not usage statistics or invented popularity claims.</p>
          </div>
          <div className="featured-tools-grid">{featuredTools.map((tool, index) => <ToolCard key={tool.id} tool={tool} featured={index === 0} />)}</div>
        </div>
      </section>

      <section className="section section--featured">
        <div className="shell">
          <div className="section-heading">
            <div><p className="eyebrow">Private money tools</p><h2>Understand spending without sharing it.</h2></div>
            <p>A full local spending, budget, CSV import, and backup workspace—without an account or bank connection.</p>
          </div>
          <div className="featured-tools-grid">{financeTools.map((tool) => <ToolCard key={tool.id} tool={tool} featured />)}</div>
        </div>
      </section>

      <section className="principles-band">
        <div className="shell principles-band__grid">
          <div>
            <p className="eyebrow eyebrow--light">The StayInBrowser standard</p>
            <h2>Local processing is a sensible default.</h2>
          </div>
          <div className="principle-list">
            <div><span>01</span><p><strong>Local first</strong>Your files are processed in your browser.</p></div>
            <div><span>02</span><p><strong>Clear explanations</strong>You can understand what a tool does before you use it.</p></div>
            <div><span>03</span><p><strong>No tool-data sharing</strong>Your files and financial records stay on your device.</p></div>
          </div>
        </div>
      </section>
    </>
  )
}
